import { useEffect, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AsyncSection,
  asyncStatus,
  Skeleton,
  Alert,
  ActionMenu,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  DescriptionItem,
  DescriptionList,
  Input,
  Modal,
  Pagination,
  Select,
  StatusPill,
  TableScroll,
  useCursorPagination,
  useRetainedValue,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { useAuthStore } from "../../auth/auth-store.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import {
  listScimOAuthClients,
  expireScimOAuthClientPreviousSecret,
  GRACE_HOURS_DEFAULT,
  GRACE_HOURS_OPTIONS,
  registerScimOAuthClient,
  revokeScimOAuthClient,
  rotateScimOAuthClientSecret,
  scimOAuthClientsQueryKey,
  scimOAuthClientsQueryKeyPrefix,
  validateScimOAuthClientLabel,
  type RegisteredScimOAuthClient,
  type ScimCredentialScope,
  type ScimOAuthClient,
  type ScimOAuthClientStatusFilter,
} from "../../api/scim-oauth-clients-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";

/* SCIM OAuth clients (PB-4543 / PPL-10), the ONLY provisioning-credential panel on
   the Identity & directory page. The static-secret sibling it was written beside has
   been withdrawn.

   PlaybookAI GENERATES the secret and shows it exactly once, so this panel does carry
   show-once machinery: a session fence on the reveal, a pre-write and post-write fence
   on the clipboard copy, and no retained copy in the mutation cache.

   An earlier revision of this header said the CUSTOMER supplied the secret and that
   none of that machinery was needed here. Both halves are now wrong, and the
   correction matters in one non-obvious place — the REQUEST body used to carry the
   plaintext, so a `mutationFn` closing over it kept it reachable from the shared
   `QueryClient` after the dialog closed. The request no longer has a secret field at
   all, but the RESPONSE now does, which is why the cache assertion still exists.
   Registration is
   therefore an imperative async flow rather than a `useMutation`; see `submit`.

   The session fence is kept on every async path, for two distinct reasons: the
   register form holds a secret the administrator typed, which must not be left on
   screen for whoever replaced them; and a completion landing after a handover must
   not adopt UI or invalidate caches for the wrong tenant. */

const SCOPE_OPTIONS: ReadonlyArray<{
  value: ScimCredentialScope;
  label: string;
}> = [
  { value: "users_and_groups", label: "People and groups" },
  { value: "users", label: "People only" },
];

const SCOPE_LABEL: Record<ScimCredentialScope, string> = {
  users_and_groups: "People and groups",
  users: "People only",
};

const STATUS_TONE = {
  active: { tone: "healthy" as const, label: "Active" },
  revoked: { tone: "neutral" as const, label: "Revoked" },
};

const CLIENT_PAGE_SIZE = 50;

function presentationFor(status: string): {
  readonly tone: "healthy" | "neutral";
  readonly label: string;
} {
  return Object.hasOwn(STATUS_TONE, status)
    ? STATUS_TONE[status as keyof typeof STATUS_TONE]
    : { tone: "neutral", label: status };
}

/* Own-property lookup, not `SCOPE_LABEL[scope] ?? scope`. The list response is
   typed but not value-validated, so a widened server vocabulary reaches this cell;
   a scope of `constructor` or `toString` would resolve through Object.prototype to
   an inherited FUNCTION, which is not nullish, so `??` never fires and React is
   handed a function as a child. Same guard as `presentationFor` above. */
function scopeLabel(scope: string): string {
  return Object.hasOwn(SCOPE_LABEL, scope)
    ? SCOPE_LABEL[scope as ScimCredentialScope]
    : scope;
}

/** Grace windows in words. Hours are what the API speaks; "7 days" is what an
    administrator plans around. */
function describeGrace(hours: number): string {
  if (hours === 0) return "Not at all — expire it now";
  if (hours === 1) return "1 hour";
  if (hours < 24) return `${hours} hours`;
  const days = hours / 24;
  return days === 1 ? "1 day" : `${days} days`;
}

/**
 * Is a rotation's predecessor still accepted?
 *
 * The PRESENCE of `previousSecretExpiresAt` is not the answer. The row keeps the
 * predecessor and its expiry after the moment passes, so treating non-null as "open"
 * left the table saying "Previous valid until <a time in the past>" and still
 * offering "End overlap" for a secret the token endpoint had already stopped
 * accepting. Found in review.
 *
 * EXCLUSIVE at the boundary, matching `isPreviousSecretUsable` in
 * `services/auth/src/admin/scim-oauth-secret.ts` exactly — at the expiry instant the
 * predecessor is already dead. The two must agree, or the UI offers an action the
 * server refuses.
 */
function isOverlapOpen(
  expiresAt: string | null,
  now: number,
  /* A TYPE PREDICATE, not just a boolean: when this is true the timestamp is
     necessarily a usable string, so the render below can format it without a second
     null check that would only exist to satisfy the compiler. */
): expiresAt is string {
  if (!expiresAt) return false;
  const at = new Date(expiresAt).getTime();
  return Number.isNaN(at) ? false : now < at;
}

interface RegisterDialogState {
  readonly scope: SessionScope;
}

interface ScopedClient {
  readonly scope: SessionScope;
  readonly client: ScimOAuthClient;
}

export function ScimOAuthClientsPanel(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr, dateTimeShort } = formatter;
  const queryClient = useQueryClient();
  const claims = useAuthStore((s) => s.claims);
  const scope = scopeFromClaims(claims);

  const [dialogState, setDialogState] = useState<RegisterDialogState | null>(
    null,
  );
  const [revokeTarget, setRevokeTarget] = useState<ScopedClient | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ScopedClient | null>(null);
  const [expireTarget, setExpireTarget] = useState<ScopedClient | null>(null);
  const [graceHours, setGraceHours] = useState<number>(GRACE_HOURS_DEFAULT);
  const [isRotating, setIsRotating] = useState(false);
  /* PB-5332 review N5. This was a `Show revoked` Switch, and it said one thing
     while doing another: ON cleared the filter entirely, so it meant "show
     ALL", not "show revoked" — and "revoked only", which the old three-way
     Select offered and the server still supports, became unreachable. That is
     the view an administrator auditing withdrawn credentials actually wants.

     A three-option Select rather than a return to the old control: it keeps the
     redesign's better DEFAULT (active-only, instead of the old page's
     everything) and matches the sibling idiom — the Activity tab filters status
     with exactly this shape. */
  const [statusFilter, setStatusFilter] = useState<
    ScimOAuthClientStatusFilter | "all"
  >("active");
  const [label, setLabel] = useState("");
  const [clientScope, setClientScope] =
    useState<ScimCredentialScope>("users_and_groups");
  const [formError, setFormError] = useState<string | null>(null);
  /* Carries the submitting scope WITH the value, like `dialogState` and
     `revokeTarget`. Fencing only the moment `setRegistered` runs is not enough:
     this state persists after the response, the panel does not unmount when the
     claims in `useAuthStore` change, and the confirmation below renders off it —
     so a handover landing AFTER the confirmation appeared would leave one
     administrator's client id, token endpoint and tenant SCIM URL on screen for
     the next. No credential, but it identifies another tenant, which is the same
     disclosure the other two dialogs are already fenced against. */
  const [registered, setRegistered] = useState<{
    readonly scope: SessionScope;
    readonly value: RegisteredScimOAuthClient;
  } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  /* Session fence at RENDER time: a handover must not leave the previous
     administrator's typed secret, their pending revoke, or their registration
     confirmation in front of whoever replaced them. Render-time, not
     set-time, because that is what survives a handover arriving later. */
  const active =
    dialogState && sameSession(dialogState.scope, scope) ? dialogState : null;
  const activeRotate =
    rotateTarget && sameSession(rotateTarget.scope, scope)
      ? rotateTarget
      : null;
  const activeExpire =
    expireTarget && sameSession(expireTarget.scope, scope)
      ? expireTarget
      : null;
  const activeRevoke =
    revokeTarget && sameSession(revokeTarget.scope, scope)
      ? revokeTarget
      : null;
  const activeRegistered =
    registered && sameSession(registered.scope, scope)
      ? registered.value
      : null;

  /* Each dialog below is hoisted onto `open` so it can animate out (ADR-0091),
     so its body renders for one more frame after the target is already null.
     The retained target keeps that frame showing the client it was about
     instead of dereferencing null — and the SESSION FENCE is re-applied AFTER
     the retention, so a handover empties the closing dialog rather than leaving
     the previous administrator's client label fading in front of the next. */
  const retainedRotate = useRetainedValue(rotateTarget);
  const shownRotate =
    retainedRotate && sameSession(retainedRotate.scope, scope)
      ? retainedRotate
      : null;
  const retainedExpire = useRetainedValue(expireTarget);
  const shownExpire =
    retainedExpire && sameSession(retainedExpire.scope, scope)
      ? retainedExpire
      : null;
  const retainedRevoke = useRetainedValue(revokeTarget);
  const shownRevoke =
    retainedRevoke && sameSession(retainedRevoke.scope, scope)
      ? retainedRevoke
      : null;

  /* "all" is the ABSENCE of a status filter, so it stays undefined on the wire
     rather than being sent as a literal the server has no enum member for. */
  const filters: { status?: ScimOAuthClientStatusFilter } =
    statusFilter === "all" ? {} : { status: statusFilter };
  const clientPaging = useCursorPagination(
    `${claims?.tenantId ?? "none"}|${filters.status ?? "all"}`,
    { pageSize: CLIENT_PAGE_SIZE },
  );
  const clients = useQuery({
    queryKey: [
      ...scimOAuthClientsQueryKey(claims?.tenantId, filters),
      clientPaging.cursor ?? "first",
    ],
    queryFn: () => listScimOAuthClients(clientPaging.cursor ?? null, filters),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      return previousKey?.[3] === claims?.tenantId &&
        previousKey?.[4] === (filters.status ?? "all")
        ? previousData
        : undefined;
    },
    /* Cosmetic only — the route is behind RequireTenantAdmin and the endpoint
       behind TenantAdminGuard. This just avoids a guaranteed 403. */
    enabled: canAdministerWorkspace(claims),
  });
  const clientRows = clients.data?.clients ?? [];

  /* A COARSE CLOCK, so a rotation overlap that lapses while the page is open stops
     being offered. `isOverlapOpen` is a comparison against now, and without a tick a
     row rendered at 16:28 would keep advertising a 16:29 deadline indefinitely.
     Thirty seconds is well inside any window an administrator would act on, and the
     interval only runs while an overlap is actually open — a table with no overlap
     schedules no timer at all, which is the common case. */
  const hasOpenOverlap = clientRows.some(
    (client) => client.previousSecretExpiresAt !== null,
  );
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!hasOpenOverlap) return;
    const tick = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(tick);
  }, [hasOpenOverlap]);

  /* No dialog-id or session fence on the CLIENT-ID copy below, unlike `copySecret`
     above. That one guards a show-once SECRET, where writing the wrong
     value to the clipboard would be a real disclosure. A client id is an
     identifier the list already displays, so the worst outcome of a late
     continuation is a "Copied" label against the wrong row — cosmetic, and not
     worth the machinery. Keyed by id rather than a boolean so two rows cannot
     both claim it. */
  const copyClientId = async (clientId: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(clientId);
      setCopiedId(clientId);
    } catch {
      /* Not surfaced: the id is on screen and selectable, so a refused clipboard
         permission is not a failure of anything the admin asked for. */
      setCopiedId(null);
    }
  };

  function resetForm(): void {
    setLabel("");
    /* Cleared explicitly on every close. React would discard the state with the
       unmounted input anyway, but the value here is a live credential and
       "something else would probably have done it" is not the standard this
       field is held to. */
    setClientScope("users_and_groups");
    setFormError(null);
  }

  /* ════════════════════════════════════════════════════════════════════════
     REGISTRATION IS IMPERATIVE, NOT `useMutation` — and that is a security fix,
     not a style preference.

     TanStack keeps a mutation's OPTIONS *and its state* on the `Mutation` object in
     the shared `MutationCache` until `gcTime` elapses — `mutationFn` and
     `state.data` alike. Generation RESPONDS with the plaintext secret, so a
     `useMutation` would leave that response body reachable from the `QueryClient`
     after the dialog closes and after a session handover, and the retained
     `mutationFn` could be invoked again to mint another.
     docs/learnings/2026-08-12-mutation-cache-holds-show-once-credentials.md is
     exactly this hazard.

     WHICH HALF CARRIES THE SECRET HAS FLIPPED, and an earlier revision of this
     comment still described the old direction: it argued the REQUEST body carried
     the customer's plaintext and that a `mutationFn` closed over it. That was true
     of the customer-supplied design. The request schema now forbids `clientSecret`
     entirely — the response is the half to protect. The conclusion is unchanged and
     the reasoning is not, which is the part worth correcting: a right answer held
     for a wrong reason survives the next refactor by luck.

     What is given up: `isPending` and error state become local, which is four
     lines. The revoke mutation below stays on `useMutation`, because it carries
     nothing but a client id.
     ════════════════════════════════════════════════════════════════════════ */
  const [isRegistering, setIsRegistering] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  /* SEPARATE from `revokeError`, and this is a fix rather than a preference. The
     first version routed a refused clipboard through `revokeError`, which renders
     only inside the revoke and rotate DIALOGS — so with the confirmation on screen
     and no dialog open, the message had nowhere to appear and a failed copy was
     silent. That is the exact outcome the handler exists to prevent, and an
     administrator who believes the secret is copied and closes the panel has lost a
     credential that cannot be read back. Caught by a test, not by review. */
  const [copyError, setCopyError] = useState<string | null>(null);
  /* SEPARATE from `copyError`, and deliberately so. `copyError` renders INSIDE the
     session-fenced confirmation, which is exactly what disappears on a handover — so
     it is the wrong place for a warning about a handover. This one renders at panel
     level, survives the confirmation being torn down, and never carries the secret,
     so it is safe to show to whoever is now signed in. */
  const [clipboardRisk, setClipboardRisk] = useState<{
    readonly cleared: boolean;
  } | null>(null);

  /**
   * Copy the show-once secret, fenced to the session that was handed it.
   *
   * TWO fences, and the ORDER is the point. An earlier revision had only the second
   * one and claimed it prevented disclosure; it did not. It ran AFTER
   * `writeText` had resolved, so a handover mid-flight still put the previous
   * tenant's live credential on a shared clipboard and the check suppressed nothing
   * but the "Copied" label. Found in review.
   *
   *   1. BEFORE the write: if the session has already changed, never disclose at
   *      all. This is the case a stale button click produces, and it is the only
   *      fence that actually prevents anything.
   *   2. AFTER the write: the handover can still land while the promise is pending,
   *      and by then the value is on the clipboard. The write cannot be recalled, so
   *      the response is to overwrite it best-effort and say so — a clipboard the
   *      new administrator finds empty is a far smaller problem than one holding
   *      someone else's SCIM credential.
   */
  async function copySecret(value: string): Promise<void> {
    const origin = scope;
    const liveNow = scopeFromClaims(useAuthStore.getState().claims);
    if (!sameSession(origin, liveNow)) {
      setCopyError(
        "Your session changed, so the secret was not copied. Generate a new one.",
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* A refused clipboard must not read as success — an administrator who
         believes the secret is copied and closes the panel has lost it. Rendered
         beside the secret, which is the only place it is useful. */
      setCopyError(
        "Could not copy to the clipboard. Select the secret and copy it manually before closing.",
      );
      return;
    }
    const live = scopeFromClaims(useAuthStore.getState().claims);
    if (!sameSession(origin, live)) {
      /* The disclosure already happened and cannot be recalled. Overwrite, and
         RECORD WHETHER THAT SUCCEEDED — an earlier revision swallowed the failure and
         told the administrator the clipboard "was cleared" either way, which is the
         one thing this path must not do: it converts a live credential still sitting
         on a shared clipboard into a reassurance. */
      let cleared: boolean;
      try {
        await navigator.clipboard.writeText("");
        cleared = true;
      } catch {
        cleared = false;
      }
      setClipboardRisk({ cleared });
      return;
    }
    setCopyError(null);
    setCopiedSecret(true);
  }

  async function submit(): Promise<void> {
    const error = validateScimOAuthClientLabel(label);
    if (error) {
      setFormError(error);
      return;
    }
    if (isRegistering) return;
    setFormError(null);
    /* The submitting session, captured before the await. Both continuations need
       it, and by the time either runs a different administrator may be signed in. */
    const origin = scope;
    setIsRegistering(true);
    try {
      const result = await registerScimOAuthClient({
        label,
        scope: clientScope,
      });
      /* The session as it is NOW, read from the store rather than from this
         closure — `scope` here is frozen at submit time, so comparing against it
         would make the fence trivially true. Same idiom as OrganizationAdmin and
         MembersAdmin. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(origin, live)) {
        /* This response NOW CARRIES A LIVE SECRET, which inverts the stakes of the
           fence below. It used to guard a client id and two URLs — a tenant
           identity leak, no more. It now guards a working credential: a handover
           landing mid-request would put the previous tenant's SECRET in front of
           whoever replaced them.

           Held in component state, deliberately, and not in a ref keyed by an
           operation id as the retired static panel did. That machinery existed
           because that panel could have several reveal dialogs in flight at once;
           here `isRegistering` admits exactly one, so an id would be a key with a
           single possible value. The session fence is what does the work. */
        setCopiedSecret(false);
        setCopyError(null);
        setRegistered({ scope: origin, value: result });
        setDialogState(null);
        resetForm();
      } else {
        /* Different administrator now. Nothing is shown, but the typed secret must
           still not survive in the form behind them. */
        resetForm();
      }
      /* Keyed on the ORIGIN tenant: the tenant that actually gained a client is
         the one whose cached list is stale. Invalidating the current render's
         tenant would refresh the replacement administrator's list and leave the
         real one showing a stale page. */
      await queryClient.invalidateQueries({
        queryKey: scimOAuthClientsQueryKeyPrefix(origin?.tenantId),
      });
    } catch (cause) {
      /* Surfaced only to the session that submitted — an error naming another
         tenant's request is noise to whoever replaced them. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(origin, live)) {
        setFormError(
          cause instanceof Error ? cause.message : "Registration failed.",
        );
      } else {
        resetForm();
      }
    } finally {
      setIsRegistering(false);
    }
  }

  const revoke = useMutation({
    /* The origin session travels with the mutation, exactly as registration's
       does. Revoke carries no secret, so `useMutation` is fine here — but the
       fence is not about secrecy: without it a handover mid-request closes
       whichever dialog is now open and refreshes the WRONG tenant's list, leaving
       the tenant that actually lost a client showing it as active. */
    mutationFn: async (variables: {
      readonly clientId: string;
      readonly scope: SessionScope;
    }) => await revokeScimOAuthClient(variables.clientId),
    onSuccess: async (_result, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) setRevokeTarget(null);
      await queryClient.invalidateQueries({
        queryKey: scimOAuthClientsQueryKeyPrefix(variables.scope?.tenantId),
      });
    },
    /* Previously absent, so a failed revoke closed nothing and said nothing — the
       administrator was left looking at an active client with no idea the request
       had failed. */
    onError: (error: Error, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) setRevokeError(error.message);
    },
  });

  /* Imperative, exactly like registration and for the same reason: the response
     carries a LIVE SECRET, and a `mutationFn` retained in the shared `QueryClient`
     until `gcTime` would keep that response reachable after the panel closed. Revoke
     and expire-previous stay on `useMutation` because neither carries one. */
  async function submitRotate(): Promise<void> {
    if (!activeRotate || isRotating) return;
    const origin = activeRotate.scope;
    const clientId = activeRotate.client.clientId;
    setIsRotating(true);
    setRevokeError(null);
    try {
      const result = await rotateScimOAuthClientSecret(clientId, graceHours);
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(origin, live)) {
        setCopiedSecret(false);
        setCopyError(null);
        /* Reuses the SAME fenced show-once container as registration. A rotation is
           an issue in every respect that matters here — a new secret, shown once,
           unrecoverable afterwards — so giving it a second reveal surface would mean
           two places to get the fence right. */
        setRegistered({ scope: origin, value: result });
        setRotateTarget(null);
        setGraceHours(GRACE_HOURS_DEFAULT);
      }
      await queryClient.invalidateQueries({
        queryKey: scimOAuthClientsQueryKeyPrefix(origin?.tenantId),
      });
    } catch (error) {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(origin, live)) {
        setRevokeError(
          error instanceof Error
            ? error.message
            : "Could not rotate the secret.",
        );
      }
    } finally {
      setIsRotating(false);
    }
  }

  const expirePrevious = useMutation({
    mutationFn: async (variables: {
      readonly clientId: string;
      readonly scope: SessionScope;
    }) => await expireScimOAuthClientPreviousSecret(variables.clientId),
    onSuccess: async (_result, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) setExpireTarget(null);
      await queryClient.invalidateQueries({
        queryKey: scimOAuthClientsQueryKeyPrefix(variables.scope?.tenantId),
      });
    },
    onError: (error: Error, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) setRevokeError(error.message);
    },
  });

  return (
    <>
      {/* Grid with a fixed 160px filter column, NOT `flex justify-between` — and
          this is a fix, not a preference. `Select` hard-codes `w-full` on its own
          wrapper, so under flex it grows to fill the row and squeezes the action
          into a box too narrow for its three-word label, which then wraps onto two
          lines. Narrowing the button would be the wrong end: the filter is the
          thing with no business being that wide. `MembersAdmin`'s toolbar pins a
          filter the same way. (This note previously cited ScimCredentialsPanel,
          which the redesign deleted; the flex row below it was only correct while
          the filter was a Switch, and came back as a bug with the Select.)

          Stacks to one column below `sm` so the control is not squeezed instead. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr] sm:items-end">
        <Select
          label="Status"
          value={statusFilter}
          options={[
            { value: "active", label: "Active" },
            { value: "revoked", label: "Revoked" },
            { value: "all", label: "All" },
          ]}
          onChange={(value) =>
            setStatusFilter(value as ScimOAuthClientStatusFilter | "all")
          }
        />
        <div className="flex sm:justify-end">
          {/* `whitespace-nowrap` so a future narrowing shrinks the neighbour
              rather than re-introducing the wrap. */}
          <Button
            variant="primary"
            className="whitespace-nowrap"
            onClick={() => {
              resetForm();
              setDialogState({ scope });
            }}
          >
            Generate credentials
          </Button>
        </div>
      </div>

      {/* OUTSIDE the session-fenced confirmation, because the handover it reports is
          what removes that confirmation. Carries no secret and no tenant-identifying
          value, so it is safe in front of whoever is now signed in. */}
      {clipboardRisk ? (
        <Alert
          tone={clipboardRisk.cleared ? "watch" : "critical"}
          className="mt-3"
        >
          <p className="font-medium">
            {clipboardRisk.cleared
              ? "Your session changed while a secret was being copied."
              : "A client secret may still be on this device's clipboard."}
          </p>
          <p className="mt-1">
            {clipboardRisk.cleared
              ? "The clipboard was overwritten, so nothing was left behind. The secret that was being copied belongs to the previous session — generate a new one if it is still needed."
              : "The session changed while the copy was in flight and the clipboard could NOT be overwritten. Clear your clipboard manually, then generate a replacement secret and revoke the one that was copied."}
          </p>
          <div className="mt-2">
            <Button variant="secondary" onClick={() => setClipboardRisk(null)}>
              Dismiss
            </Button>
          </div>
        </Alert>
      ) : null}

      {activeRegistered ? (
        <Alert tone="healthy" className="mt-3">
          <p className="font-medium">
            Registered “{activeRegistered.client.label}”.
          </p>
          <p className="mt-1">
            <strong>Copy the configuration now.</strong> The secret is shown
            once and {PRODUCT_NAME} keeps only a one-way hash of it — there is
            no way to read it back, and no support path that can recover it. If
            it is lost, rotate the secret to get a new one.
          </p>
          {/* Look row OK-25: the label/value grid is the DescriptionList. */}
          <DescriptionList
            layout="horizontal"
            density="compact"
            className="mt-2"
          >
            <DescriptionItem label="Client ID">
              {activeRegistered.client.clientId}
            </DescriptionItem>
            <DescriptionItem label="Token endpoint">
              {activeRegistered.tokenEndpoint}
            </DescriptionItem>
            <DescriptionItem label="Tenant URL">
              {activeRegistered.scimBaseUrl}
            </DescriptionItem>
            {/* THE show-once value. Rendered inside the same session-fenced
                container as the rest, so a handover clears it along with them —
                and it is the reason that fence now guards a working credential
                rather than a tenant identity.

                Not a password input: this is a value to be copied out, and
                masking it would only add a reveal click while the plaintext sits
                in the DOM either way. */}
            <DescriptionItem label="Client secret">
              <span className="font-mono">{activeRegistered.clientSecret}</span>
            </DescriptionItem>
            {/* The confirmation half of the transfer: whoever receives the secret
                derives the same 8 characters from their copy and compares, which
                proves it arrived intact without either party re-reading it. Also
                what the list below shows, so an administrator can later tell WHICH
                secret a provider is holding. */}
            <DescriptionItem label="Secret fingerprint">
              <span className="font-mono">
                {activeRegistered.secretFingerprint}
              </span>
            </DescriptionItem>
          </DescriptionList>
          {copyError ? (
            <p role="alert" className="mt-2 text-sm text-pb-red-text">
              {copyError}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="primary"
              onClick={() =>
                void copySecret(scimConfigurationJson(activeRegistered))
              }
            >
              {copiedSecret ? "Configuration copied" : "Copy configuration"}
            </Button>
            {/* ONE dismissal control, beside the copy action it follows. There were
                two — "Done" here and a separate "Dismiss" below — with identical
                behaviour in the same alert, which review flagged as ambiguous.

                Explicit rather than a timer: an administrator pasting into another
                window must not lose the value to a countdown. */}
            <Button variant="ghost" onClick={() => setRegistered(null)}>
              Done
            </Button>
          </div>
        </Alert>
      ) : null}

      <Card className="mt-3">
        <CardBody flush>
          {/* Was a bare red paragraph carrying the server's sentence and no way
              back — a sixth way of saying "this failed". */}
          {(clients.isPending && clients.isFetching) || clients.error ? (
            <AsyncSection
              status={asyncStatus({
                isLoading: clients.isPending && clients.isFetching,
                isError: Boolean(clients.error),
                isEmpty: false,
                hasData: clientRows.length > 0,
              })}
              loadingLabel="the OAuth clients"
              errorTitle="Couldn't load the OAuth clients"
              errorDescription={clients.error?.message}
              onRetry={() => {
                clients.refetch();
              }}
              skeleton={<Skeleton className="m-4 h-16" />}
            >
              {null}
            </AsyncSection>
          ) : null}
          {/* `isEmpty`, not `items.length === 0`: the latter is also true while
              the first page is in flight, which would flash "no OAuth clients
              yet" at an admin who has several. */}
          {clients.isSuccess && clientRows.length === 0 && !clients.error ? (
            <p className="p-4 text-sm text-pb-text-muted">
              No OAuth clients yet. Generate credentials to give your identity
              provider a client ID and secret it can authenticate with. The
              secret is shown once.
            </p>
          ) : null}

          {clientRows.length > 0 ? (
            /* `overflow-x-auto` is what makes `whitespace-nowrap` safe below: every
               header and every control stays on one line, and if the viewport cannot
               fit them the TABLE scrolls rather than the page. Wrapped headers and
               two-line buttons were the whole of what made this read as unfinished. */
            <TableScroll aria-label="SCIM OAuth clients" embedded>
              {/* eslint-disable-next-line ds/no-hand-built -- DataTable would restyle this header (uppercase 10px labels) and cell padding; no PR 3 look row approves that, so the raw table stays inside TableScroll. */}
              <table className="w-full text-dense-1">
                <thead className="bg-pb-card-elevated">
                  <tr className="border-b border-pb-border">
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">
                      Name
                    </th>
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">
                      Status
                    </th>
                    {/* "Current secret", not "Secret": the cell holds a fingerprint of
                        the secret the provider should be holding RIGHT NOW, which is
                        the question a rotation raises. Naming it "Secret" invited the
                        fair reading that the column was pointless. */}
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">
                      Current secret
                    </th>
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">
                      May provision
                    </th>
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">
                      {/* One disclosure for the whole table: the overlap
                          deadline in the Secret column is in this zone too. */}
                      <AdminTimeZoneLabel
                        label="Last used"
                        formatter={formatter}
                      />
                    </th>
                    <th className="whitespace-nowrap px-4 py-2 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientRows.map((client) => {
                    const presentation = presentationFor(client.status);
                    return (
                      <tr
                        key={client.clientId}
                        className="border-b border-pb-border transition-colors hover:pb-overlay-paint last:border-0"
                      >
                        {/* The label leads; the client id is secondary. A full UUID
                          on every row reads as debug output and pushes the columns
                          that carry meaning off to the right — but it cannot just
                          be hidden, because it is the value an administrator
                          pastes into their identity provider. So: truncated,
                          monospace, and copyable in one click, with the full value
                          on the element's title for anyone who wants to read it. */}
                        <td className="px-4 py-2 align-top">
                          <div className="font-medium">{client.label}</div>
                          <div className="mt-0.5 flex items-center gap-2 whitespace-nowrap">
                            <code
                              title={client.clientId}
                              className="font-mono text-pb-text-muted"
                            >
                              {client.clientId.slice(0, 8)}…
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              /* A quiet control, not an underlined inline link: it sits on
                               every row, so link styling turned the identifier column
                               into a column of blue text. */
                              className="h-6 px-1.5 text-dense-1 text-pb-text-muted hover:text-pb-text"
                              /* The accessible name tracks the STATE, not just the
                               action. A static "Copy client ID for X" would leave a
                               screen-reader user with no confirmation that the copy
                               happened — the "Copied" feedback is visual only, and an
                               aria-label overrides the visible text entirely. */
                              aria-label={
                                copiedId === client.clientId
                                  ? `Client ID copied for ${client.label}`
                                  : `Copy client ID for ${client.label}`
                              }
                              onClick={() => void copyClientId(client.clientId)}
                            >
                              {copiedId === client.clientId
                                ? "Copied"
                                : "Copy ID"}
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-2 align-top">
                          <StatusPill tone={presentation.tone}>
                            {presentation.label}
                          </StatusPill>
                        </td>
                        <td className="px-4 py-2">
                          {client.secretFingerprint ? (
                            /* DS keep: today's 18px chip; Badge code is 16px tall and takes no size. */
                            <code
                              translate="no"
                              className="inline-flex h-4.5 items-center gap-1 whitespace-nowrap rounded-md bg-pb-card-elevated px-1.5 font-mono text-dense-1 font-normal text-pb-text"
                            >
                              {client.secretFingerprint}
                            </code>
                          ) : (
                            /* Only a pre-redesign row can be null. Shown as unknown
                             rather than blank, because a blank cell reads as "no
                             secret" and this client certainly has one. */
                            <span className="italic text-pb-text-muted">
                              Unknown
                            </span>
                          )}
                          {/* An OPEN ROTATION OVERLAP, surfaced because an overlap
                            nobody can see is an overlap nobody closes. Two secrets
                            are valid until this moment passes — and once it has,
                            this disappears rather than reporting a past deadline. */}
                          {isOverlapOpen(
                            client.previousSecretExpiresAt,
                            nowMs,
                          ) ? (
                            <div className="mt-1 text-dense-1 text-pb-text-muted">
                              Previous valid until{" "}
                              {dateTimeShort(client.previousSecretExpiresAt)}
                            </div>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 align-top">
                          {scopeLabel(client.scope)}
                        </td>
                        {/* "Waiting for first connection" rather than an em dash:
                          null here means the identity provider has never
                          authenticated, which is a state an admin can ACT on
                          (their provisioning is not configured yet) — and it is
                          also the UI-visible proof that a token exchange worked,
                          since this is what fills in. */}
                        <td className="whitespace-nowrap px-4 py-2 align-top text-pb-text-muted">
                          {client.lastUsedAt ? (
                            dateTimeOr(client.lastUsedAt, "—")
                          ) : (
                            /* Still the actionable phrasing rather than an em dash, but
                             short enough to sit on one line — it was wrapping to three. */
                            <span className="italic">Not yet connected</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right align-top">
                          <div className="flex justify-end">
                            <ActionMenu
                              triggerSize="sm"
                              triggerLabel={`Actions for ${client.label}`}
                              items={[
                                {
                                  id: "rotate",
                                  label: "Rotate secret",
                                  ...(client.status !== "active"
                                    ? {
                                        disabledReason:
                                          "This client is revoked.",
                                      }
                                    : {}),
                                  onSelect: () => {
                                    setRevokeError(null);
                                    setRotateTarget({ scope, client });
                                  },
                                },
                                {
                                  id: "end-overlap",
                                  label: "End overlap",
                                  ...(!isOverlapOpen(
                                    client.previousSecretExpiresAt,
                                    nowMs,
                                  )
                                    ? {
                                        disabledReason:
                                          "No rotation overlap is active.",
                                      }
                                    : {}),
                                  onSelect: () => {
                                    setRevokeError(null);
                                    setExpireTarget({ scope, client });
                                  },
                                },
                                {
                                  id: "revoke",
                                  label: "Revoke",
                                  tone: "critical",
                                  separatorBefore: true,
                                  ...(client.status !== "active"
                                    ? {
                                        disabledReason:
                                          "This client is already revoked.",
                                      }
                                    : {}),
                                  onSelect: () => {
                                    setRevokeError(null);
                                    setRevokeTarget({ scope, client });
                                  },
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          ) : null}

          {clientPaging.page > 1 || clients.data?.nextCursor ? (
            <div className="flex justify-end p-3">
              <Pagination
                mode="cursor"
                page={clientPaging.page}
                pageItemCount={clientRows.length}
                canGoBack={clientPaging.canGoBack}
                hasNext={Boolean(clients.data?.nextCursor)}
                busy={clients.isFetching}
                onPrev={clientPaging.goBack}
                onNext={() => {
                  const cursor = clients.data?.nextCursor;
                  if (cursor) clientPaging.goNext(cursor);
                }}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>

      {/* The body reads only form state, which the panel owns, so it is already
          null-safe on the closing frame and needs no retained value. */}
      <Modal
        open={active !== null}
        title="Generate credentials"
        onClose={() => {
          setDialogState(null);
          resetForm();
        }}
      >
        <div className="grid gap-3">
          <p className="text-sm text-pb-text-muted">
            {PRODUCT_NAME} generates the client id and secret. The secret is
            shown once, on the next screen — copy it into your identity provider
            before closing, because it cannot be retrieved afterwards.
          </p>
          <label className="grid gap-1">
            <span className="text-dense-1 font-medium">Name</span>
            <Input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Entra provisioning"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-dense-1 font-medium">May provision</span>
            <Select
              value={clientScope}
              options={SCOPE_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              onChange={(value) => setClientScope(value as ScimCredentialScope)}
            />
          </label>
          {formError ? (
            <p role="alert" className="text-sm text-pb-red-text">
              {formError}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={isRegistering}
            >
              {isRegistering ? "Registering..." : "Register"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDialogState(null);
                resetForm();
              }}
              disabled={isRegistering}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {shownExpire ? (
        <ConfirmDialog
          open={activeExpire !== null}
          title="Expire the previous secret"
          tone="neutral"
          confirmLabel={
            expirePrevious.isPending ? "Expiring…" : "Expire it now"
          }
          loading={expirePrevious.isPending}
          onClose={() => {
            setExpireTarget(null);
            setRevokeError(null);
          }}
          onConfirm={() => {
            if (!activeExpire) return;
            expirePrevious.mutate({
              clientId: activeExpire.client.clientId,
              scope: activeExpire.scope,
            });
          }}
        >
          <p>
            The previous secret for “{shownExpire.client.label}” will stop
            working immediately, before{" "}
            <AdminTimeZoneLabel
              label={dateTimeOr(
                shownExpire.client.previousSecretExpiresAt,
                "—",
              )}
              formatter={formatter}
              variant="inline"
            />
            . If your identity provider is still using it, provisioning will
            fail until it holds the current secret. This cannot be undone — the
            overlap window cannot be reopened.
          </p>
          {revokeError ? (
            <p role="alert" className="mt-2 text-sm text-pb-red-text">
              {revokeError}
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}

      {shownRotate ? (
        <Modal
          open={activeRotate !== null}
          title="Rotate client secret"
          onClose={() => {
            setRotateTarget(null);
            setRevokeError(null);
            setGraceHours(GRACE_HOURS_DEFAULT);
          }}
        >
          <div className="grid gap-3">
            <p className="text-sm">
              {PRODUCT_NAME} will issue a new secret for “
              {shownRotate.client.label}” and show it once. The client ID does
              not change, so you only need to update the secret in your identity
              provider.
            </p>
            <label className="grid gap-1">
              <span className="text-dense-1 font-medium">
                Keep the current secret working for
              </span>
              {/* THE OPERATOR'S CHOICE, and the reason this control exists rather
                  than a fixed window: only they know when their provider will be
                  updated. A vendor-imposed deadline turns rotation into scheduled
                  maintenance, it gets deferred, and deferred rotation means stale
                  secrets live longer. Same model as AWS access keys, Azure AD app
                  registrations and Stripe rolling keys. */}
              <Select
                value={String(graceHours)}
                options={GRACE_HOURS_OPTIONS.map((hours) => ({
                  value: String(hours),
                  label: describeGrace(hours),
                }))}
                onChange={(value) => setGraceHours(Number(value))}
              />
            </label>
            <p className="text-dense-1 text-pb-text-muted">
              {graceHours === 0
                ? "The current secret stops working immediately. Choose this if you think it has leaked — provisioning will fail until you paste the new one into your provider."
                : "Both secrets are accepted during this window, so provisioning keeps working while you update your provider. You can end it early with “End overlap”."}
            </p>
          </div>
          {revokeError ? (
            <p role="alert" className="mt-2 text-sm text-pb-red-text">
              {revokeError}
            </p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setRotateTarget(null);
                setRevokeError(null);
                setGraceHours(GRACE_HOURS_DEFAULT);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isRotating}
              onClick={() => void submitRotate()}
            >
              {isRotating ? "Rotating…" : "Rotate secret"}
            </Button>
          </div>
        </Modal>
      ) : null}

      {shownRevoke ? (
        /* A revoke is a removal: the destructive ConfirmDialog (d2), whose red
           confirm IS this dialog's accent (invariant 9). */
        <ConfirmDialog
          open={activeRevoke !== null}
          title="Revoke OAuth client"
          confirmLabel={revoke.isPending ? "Revoking..." : "Revoke"}
          loading={revoke.isPending}
          onClose={() => {
            setRevokeTarget(null);
            setRevokeError(null);
          }}
          onConfirm={() => {
            if (!activeRevoke) return;
            setRevokeError(null);
            revoke.mutate({
              clientId: activeRevoke.client.clientId,
              scope,
            });
          }}
        >
          <p>
            “{shownRevoke.client.label}” will stop working immediately,
            including any access token it has already been issued. Your identity
            provider will fail to provision until it is repointed at another
            client.
          </p>
          {revokeError ? (
            <p role="alert" className="mt-2 text-sm text-pb-red-text">
              {revokeError}
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

/** The portable one-time payload an administrator pastes into their IdP. */
export function scimConfigurationJson(
  registered: RegisteredScimOAuthClient,
): string {
  return JSON.stringify(
    {
      clientId: registered.client.clientId,
      clientSecret: registered.clientSecret,
      tokenEndpoint: registered.tokenEndpoint,
      scimBaseUrl: registered.scimBaseUrl,
      scope: registered.client.scope,
    },
    null,
    2,
  );
}
